import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthService } from '@brightside/core/auth';
import { environment } from '@brighthub/environment';
import { firstValueFrom, Observable } from 'rxjs';
import {
  ConversationalBtmAcceptedRun,
  ConversationalBtmApiErrorBody,
  ConversationalBtmConversationDetail,
  ConversationalBtmConversationPage,
  ConversationalBtmSubmitRequest,
} from './conversational-btm.models';
import {
  ConversationalBtmRunMessage,
  ConversationalBtmRunState,
  conversationalBtmRunMessages,
  createConversationalBtmRunState,
  processConversationalBtmRunStream,
} from './run-message-reducer';

@Injectable({ providedIn: 'root' })
export class ConversationalBtmApiService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly baseUrl = requireBaseUrl();

  async submit(
    body: ConversationalBtmSubmitRequest,
    idempotencyKey: string
  ): Promise<ConversationalBtmAcceptedRun> {
    return this.request((headers) =>
      this.http.post<ConversationalBtmAcceptedRun>(
        `${this.baseUrl}/conversations`,
        body,
        {
          headers: headers
            .set('Content-Type', 'application/json')
            .set('Idempotency-Key', idempotencyKey),
        }
      )
    );
  }

  async listConversations(
    options: { limit?: number; after?: string } = {}
  ): Promise<ConversationalBtmConversationPage> {
    const params = new URLSearchParams({
      limit: String(options.limit ?? 20),
    });
    if (options.after) params.set('after', options.after);
    return this.request((headers) =>
      this.http.get<ConversationalBtmConversationPage>(
        `${this.baseUrl}/conversations?${params}`,
        { headers }
      )
    );
  }

  async deleteConversation(conversationId: string): Promise<void> {
    await this.request((headers) =>
      this.http.delete<void>(
        `${this.baseUrl}/conversations/${encodeURIComponent(conversationId)}`,
        { headers }
      )
    );
  }

  async getConversationPage(
    conversationId: string,
    options: { limit?: number; after?: string } = {}
  ): Promise<ConversationalBtmConversationDetail> {
    const params = new URLSearchParams({
      format: 'parts',
      limit: String(options.limit ?? 20),
    });
    if (options.after) params.set('after', options.after);
    return this.request((headers) =>
      this.http.get<ConversationalBtmConversationDetail>(
        `${this.baseUrl}/conversations/${encodeURIComponent(
          conversationId
        )}?${params}`,
        { headers }
      )
    );
  }

  async getConversation(
    conversationId: string
  ): Promise<ConversationalBtmConversationDetail> {
    return this.getConversationPage(conversationId);
  }

  async getCompleteConversation(
    conversationId: string,
    limit = 20
  ): Promise<ConversationalBtmConversationDetail> {
    let detail = await this.getConversationPage(conversationId, { limit });
    const seenCursors = new Set<string>();
    while (detail.has_more && detail.next_cursor) {
      if (seenCursors.has(detail.next_cursor)) break;
      seenCursors.add(detail.next_cursor);
      const older = await this.getConversationPage(conversationId, {
        limit,
        after: detail.next_cursor,
      });
      detail = mergeConversationalBtmConversationPages(detail, older);
    }
    return detail;
  }

  async openRunStream(options: {
    streamUrl: string;
    lastEventId?: string;
    signal: AbortSignal;
  }): Promise<Response> {
    const headers = await this.authorizationHeaders();
    const response = await fetch(this.resolveStreamUrl(options.streamUrl), {
      headers: {
        Accept: 'text/event-stream',
        Authorization: headers.get('Authorization') as string,
        ...(options.lastEventId
          ? { 'Last-Event-ID': options.lastEventId }
          : {}),
      },
      signal: options.signal,
    });
    if (!response.ok) throw await responseError(response);
    if (!response.body) {
      throw new ConversationalBtmApiError(
        'The run stream returned no response body.',
        { status: response.status, code: 'EMPTY_STREAM' }
      );
    }
    return response;
  }

  async followRun(options: {
    conversationId: string;
    runId: string;
    streamUrl: string;
    signal: AbortSignal;
    initialMessages?: readonly ConversationalBtmRunMessage[];
    initialLastEventId?: string;
    retryDelaysMs?: readonly number[];
    onConnectionChange?: (
      status: 'connecting' | 'streaming' | 'reconnecting' | 'reconciling',
      attempt: number
    ) => void;
    onSnapshot?: (state: ConversationalBtmRunState) => void;
  }): Promise<{
    detail: ConversationalBtmConversationDetail;
    state: ConversationalBtmRunState;
  }> {
    const retryDelays = options.retryDelaysMs ?? [500, 1_000, 2_000];
    let runId = options.runId;
    let streamUrl = options.streamUrl;
    let state = {
      ...createConversationalBtmRunState(runId, options.initialMessages),
      ...(options.initialLastEventId
        ? { lastEventId: options.initialLastEventId }
        : {}),
    };

    for (let attempt = 0; ; attempt += 1) {
      throwIfAborted(options.signal);
      try {
        options.onConnectionChange?.('connecting', attempt);
        const response = await this.openRunStream({
          streamUrl,
          lastEventId: state.lastEventId,
          signal: options.signal,
        });
        options.onConnectionChange?.('streaming', attempt);
        const result = await processConversationalBtmRunStream({
          response,
          initialState: state,
          onSnapshot: (snapshot) => {
            state = snapshot;
            options.onSnapshot?.(snapshot);
          },
        });
        state = result.state;
        if (result.terminal !== null) {
          options.onConnectionChange?.('reconciling', attempt);
          return {
            detail: await this.getCompleteConversation(options.conversationId),
            state,
          };
        }
      } catch (error) {
        if (isAbort(error) || options.signal.aborted) throw error;
        if (!isRetryableRunError(error)) throw error;
      }

      if (attempt >= retryDelays.length) {
        throw new ConversationalBtmApiError(
          'The reply stream disconnected before the run finished.',
          { status: 0, code: 'RUN_DISCONNECTED' }
        );
      }

      options.onConnectionChange?.('reconnecting', attempt + 1);
      await wait(retryDelays[attempt] ?? 0, options.signal);
      let detail: ConversationalBtmConversationDetail;
      try {
        detail = await this.getConversation(options.conversationId);
      } catch (error) {
        if (!isRetryableRunError(error)) throw error;
        continue;
      }
      const execution = detail.conversation.execution;
      if (
        execution.status !== 'queued' &&
        execution.status !== 'running'
      ) {
        return { detail, state };
      }
      if (!execution.run_id || !execution.stream_url) {
        throw new ConversationalBtmApiError(
          'The active run is missing its stream location.',
          { status: 502, code: 'INVALID_EXECUTION' }
        );
      }

      if (execution.run_id !== runId) {
        runId = execution.run_id;
        state = createConversationalBtmRunState(
          runId,
          durableRunMessages(detail, runId)
        );
      } else if (conversationalBtmRunMessages(state).length === 0) {
        state = createConversationalBtmRunState(
          runId,
          durableRunMessages(detail, runId)
        );
      }
      streamUrl = execution.stream_url;
    }
  }

  resolveStreamUrl(streamUrl: string): string {
    const base = new URL(this.baseUrl);
    let resolved: URL;
    try {
      if (/^https?:\/\//i.test(streamUrl)) {
        resolved = new URL(streamUrl);
      } else if (streamUrl.startsWith('/api/v1/')) {
        resolved = new URL(streamUrl, base.origin);
      } else if (streamUrl.startsWith('/')) {
        resolved = new URL(`${base.pathname.replace(/\/$/, '')}${streamUrl}`, base.origin);
      } else {
        throw new Error('Relative path must start with a slash.');
      }
    } catch {
      throw new ConversationalBtmApiError(
        'The conversational API returned an invalid stream URL.',
        { status: 502, code: 'INVALID_STREAM_URL' }
      );
    }
    if (resolved.origin !== base.origin) {
      throw new ConversationalBtmApiError(
        'The conversational API returned a stream URL for another origin.',
        { status: 502, code: 'INVALID_STREAM_ORIGIN' }
      );
    }
    return resolved.toString();
  }

  private async request<T>(
    makeRequest: (headers: HttpHeaders) => Observable<T>
  ): Promise<T> {
    const headers = await this.authorizationHeaders();
    try {
      return await firstValueFrom(makeRequest(headers));
    } catch (error) {
      if (error instanceof ConversationalBtmApiError) throw error;
      throw httpError(error);
    }
  }

  private async authorizationHeaders(): Promise<HttpHeaders> {
    const token = await this.auth.getAccessToken();
    if (!token) {
      throw new ConversationalBtmApiError('Authentication is required.', {
        status: 401,
        code: 'AUTHENTICATION_REQUIRED',
      });
    }
    return new HttpHeaders({
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    });
  }
}

export class ConversationalBtmApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly requestId?: string;

  constructor(
    message: string,
    options: { status: number; code?: string; requestId?: string }
  ) {
    super(message);
    this.name = 'ConversationalBtmApiError';
    this.status = options.status;
    this.code = options.code;
    this.requestId = options.requestId;
  }
}

export function mergeConversationalBtmConversationPages(
  newer: ConversationalBtmConversationDetail,
  older: ConversationalBtmConversationDetail
): ConversationalBtmConversationDetail {
  const messages = new Map(
    [...older.messages, ...newer.messages].map((message) => [message.id, message])
  );
  return {
    ...newer,
    messages: [...messages.values()],
    next_cursor: older.next_cursor,
    has_more: older.has_more,
  };
}

function requireBaseUrl(): string {
  const baseUrl = environment.apiEndpoints['conversational-btm'];
  if (!baseUrl) {
    throw new ConversationalBtmApiError(
      'The conversational API endpoint is not configured.',
      { status: 0, code: 'API_NOT_CONFIGURED' }
    );
  }
  return baseUrl.replace(/\/$/, '');
}

async function responseError(response: Response): Promise<ConversationalBtmApiError> {
  let body: ConversationalBtmApiErrorBody | undefined;
  try {
    body = (await response.json()) as ConversationalBtmApiErrorBody;
  } catch {
    body = undefined;
  }
  return new ConversationalBtmApiError(
    body?.error?.message ??
      `Conversational API request failed (${response.status}).`,
    {
      status: response.status,
      code: body?.error?.code,
      requestId:
        body?.error?.request_id ??
        response.headers.get('x-request-id') ??
        undefined,
    }
  );
}

function httpError(error: unknown): ConversationalBtmApiError {
  if (!(error instanceof HttpErrorResponse)) {
    return new ConversationalBtmApiError('Conversational API request failed.', {
      status: 0,
      code: 'NETWORK_ERROR',
    });
  }
  const body = error.error as ConversationalBtmApiErrorBody | undefined;
  return new ConversationalBtmApiError(
    body?.error?.message ??
      `Conversational API request failed (${error.status}).`,
    {
      status: error.status,
      code: body?.error?.code,
      requestId:
        body?.error?.request_id ?? error.headers?.get('x-request-id') ?? undefined,
    }
  );
}

function durableRunMessages(
  detail: ConversationalBtmConversationDetail,
  runId: string
): ConversationalBtmRunMessage[] {
  return detail.messages.flatMap((message) =>
    message.run_id === runId && message.role === 'assistant'
      ? [
          {
            id: message.id,
            content: message.content ?? '',
            parts: message.parts ?? [],
          },
        ]
      : []
  );
}

function isRetryableRunError(error: unknown): boolean {
  if (!(error instanceof ConversationalBtmApiError)) return true;
  return (
    error.status === 0 ||
    error.status === 408 ||
    error.status === 410 ||
    error.status === 429 ||
    error.status >= 500
  );
}

function isAbort(error: unknown): boolean {
  return (
    error instanceof DOMException
      ? error.name === 'AbortError'
      : typeof error === 'object' &&
        error !== null &&
        (error as { name?: unknown }).name === 'AbortError'
  );
}

function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) {
    throw new DOMException('The operation was aborted.', 'AbortError');
  }
}

function wait(milliseconds: number, signal: AbortSignal): Promise<void> {
  if (milliseconds <= 0) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      signal.removeEventListener('abort', abort);
      resolve();
    }, milliseconds);
    const abort = () => {
      clearTimeout(timeout);
      reject(new DOMException('The operation was aborted.', 'AbortError'));
    };
    signal.addEventListener('abort', abort, { once: true });
  });
}
